/**
 * Cart Visibility Manager
 * Handles visibility of cart upsell card and cart reviews based on cart value and upsell product presence
 */
class CartVisibilityManager {
  constructor() {
    this.cartValueThreshold = 29700; // 297.00 in cents
    this.upsellProductId = null;
    this.isCheckingVisibility = false; // Prevent multiple simultaneous checks
    this.visibilityCheckTimeout = null; // For debouncing
    this.isCartUpdating = false; // Track if cart is currently updating
    this.cartUpdateTimeout = null; // Timeout to reset cart updating state
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupUpsellProductId();
    this.setupMutationObserver();
    // Delay initial visibility check to ensure DOM and cart are ready
    setTimeout(() => this.checkVisibility(), 500);
    // Apply promotional item styles on page load
    setTimeout(() => this.hidePromotionalItemQuantitySelectors(), 1000);
  }

  bindEvents() {
    // Handle variant selection for upsell card
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-upsell-variant-select]')) {
        this.handleVariantChange(e.target);
      }
    });

    // Listen for cart refresh events from the theme
    document.addEventListener('cart:refresh', (e) => {
      if (e.detail && e.detail.source !== 'visibility-manager') {
        this.refreshCart();
        // Don't call checkVisibility here - let the refresh complete first
      }
    });

    // Listen for cart content replacement events (when API cart items are updated)
    document.addEventListener('cart:content:replaced', () => {
      console.log('CartVisibilityManager: Cart content replaced, re-applying visibility logic');
      // Small delay to ensure the new content is fully rendered
      setTimeout(() => this.checkVisibility(), 200);
    });

    // Listen for cart drawer open events
    document.addEventListener('cart:open', () => {
      this.refreshCart();
      // Delay visibility check to let cart data load
      setTimeout(() => this.checkVisibility(), 300);
    });

    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      // Mark cart update as completed and check visibility
      this.setCartUpdating(false);
      console.log('CartVisibilityManager: Cart update completed, checking visibility');
      // Hide quantity selectors and show remove buttons for promotional items
      this.hidePromotionalItemQuantitySelectors();
      this.checkVisibility();
    });

    // Listen for cart item removal
    document.addEventListener('cart:item:removed', () => {
      // Mark cart update as completed and check visibility
      this.setCartUpdating(false);
      console.log('CartVisibilityManager: Cart item removal completed, checking visibility');
      // Hide quantity selectors and show remove buttons for promotional items
      this.hidePromotionalItemQuantitySelectors();
      this.checkVisibility();
    });

    // Listen for cart changes (general cart modifications)
    document.addEventListener('cart:change', () => {
      // Mark cart update as completed and check visibility
      this.setCartUpdating(false);
      console.log('CartVisibilityManager: Cart change completed, checking visibility');
      // Hide quantity selectors and show remove buttons for promotional items
      this.hidePromotionalItemQuantitySelectors();
      this.checkVisibility();
    });

    // Listen for theme cart update events
    document.addEventListener('theme:cart:update', () => {
      // Mark cart update as started
      this.setCartUpdating(true);
    });

    // Listen for quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-quantity]')) {
        // Mark cart update as started
        this.setCartUpdating(true);
        console.log('CartVisibilityManager: Quantity change started, preventing visibility changes');
      }
    });

    // Listen for add to cart form submissions
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        // Check if this is an upsell form
        const upsellCard = e.target.closest('[data-cart-upsell]');
        if (upsellCard) {
          // Set loading state for upsell forms
          this.setFormLoadingState(e.target, true);
          // Mark cart update as started for upsell forms
          this.setCartUpdating(true);
          console.log('CartVisibilityManager: Upsell form submitted, preventing visibility changes');
        } else {
          // For non-upsell forms, just check visibility normally
          this.debouncedCheckVisibility();
        }
      }
    });

    // Handle add to cart button clicks for dynamic updates
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        const upsellCard = e.target.closest('[data-cart-upsell]');
        if (upsellCard) {
          // For upsell cards, the form submission will handle visibility
          return;
        }
        // For other add to cart buttons, check visibility after a delay
        this.debouncedCheckVisibility();
      }
    });

    // Handle button focus states for accessibility
    document.addEventListener('focusin', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        e.target.style.outline = '2px solid #6fb576';
        e.target.style.outlineOffset = '2px';
      }
    });

    document.addEventListener('focusout', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        e.target.style.outline = '';
        e.target.style.outlineOffset = '';
      }
    });
  }

  /**
   * Debounced visibility check to prevent multiple rapid calls
   */
  debouncedCheckVisibility() {
    if (this.visibilityCheckTimeout) {
      clearTimeout(this.visibilityCheckTimeout);
    }
    this.visibilityCheckTimeout = setTimeout(() => {
      this.checkVisibility();
    }, 150);
  }

  /**
   * Set cart updating state with automatic timeout reset
   */
  setCartUpdating(isUpdating) {
    // Clear any existing timeout
    if (this.cartUpdateTimeout) {
      clearTimeout(this.cartUpdateTimeout);
      this.cartUpdateTimeout = null;
    }

    this.isCartUpdating = isUpdating;
    
    if (isUpdating) {
      console.log('CartVisibilityManager: Cart update started, preventing visibility changes');
      // Set a timeout to automatically reset the cart updating state
      // This prevents the cart from being stuck in updating state forever
      this.cartUpdateTimeout = setTimeout(() => {
        console.log('CartVisibilityManager: Cart update timeout reached, forcing state reset');
        this.isCartUpdating = false;
        // Now check visibility since cart should be stable
        this.checkVisibility();
      }, 3000); // 3 second timeout
    } else {
      console.log('CartVisibilityManager: Cart update completed, allowing visibility changes');
    }
  }

  /**
   * Hide quantity selectors and show remove buttons for promotional items with $.99 selling plan
   */
  hidePromotionalItemQuantitySelectors() {
    // Find all cart items
    const cartItems = document.querySelectorAll('.cart__item');
    
    cartItems.forEach(item => {
      // Check if this item has the promotional selling plan text
      const sellingPlanElement = item.querySelector('.cart__item__selling-plan span');
      
      if (sellingPlanElement && sellingPlanElement.textContent.includes('Try Electrolytes for $.99 ONLY')) {
        // Add promotional item attribute for CSS targeting
        item.setAttribute('data-promotional-item', 'true');
        item.classList.add('cart__item--promotional');
        
        // Find the quantity selector for this item
        const quantitySelector = item.querySelector('.cart__quantity-counter');
        
        if (quantitySelector) {
          // Hide the quantity selector
          quantitySelector.style.display = 'none';
          console.log('CartVisibilityManager: Hidden quantity selector for promotional item:', item.dataset.itemTitle || 'Unknown');
        }
        
        // Find and show the remove button for this item
        const removeButton = item.querySelector('.cart__item__remove');
        if (removeButton) {
          removeButton.classList.remove('hidden');
          removeButton.style.display = 'block';
          removeButton.style.opacity = '1';
          removeButton.style.visibility = 'visible';
          console.log('CartVisibilityManager: Shown remove button for promotional item:', item.dataset.itemTitle || 'Unknown');
        }
      }
    });
  }

  /**
   * Show loading state for cart elements during updates
   */
  showLoadingState() {
    // Show loading for cart upsell card
    const upsellCards = document.querySelectorAll('[data-cart-upsell]');
    upsellCards.forEach(card => {
      if (!card.querySelector('.cart-loading-indicator')) {
        const loadingIndicator = document.createElement('div');
        loadingIndicator.className = 'cart-loading-indicator';
        loadingIndicator.innerHTML = `
          <div class="loading-spinner">
            <div class="spinner"></div>
            <span>Updating cart...</span>
          </div>
        `;
        card.appendChild(loadingIndicator);
      }
    });

    // Show loading for cart reviews
    const cartReviews = document.querySelectorAll('.CP_TE_Sitewide_07_V2');
    cartReviews.forEach(review => {
      if (!review.querySelector('.cart-loading-indicator')) {
        const loadingIndicator = document.createElement('div');
        loadingIndicator.className = 'cart-loading-indicator';
        loadingIndicator.innerHTML = `
          <div class="loading-spinner">
            <div class="spinner"></div>
            <span>Updating cart...</span>
          </div>
        `;
        review.appendChild(loadingIndicator);
      }
    });

    console.log('CartVisibilityManager: Showing loading state for cart elements');
  }

  /**
   * Hide loading state for cart elements
   */
  hideLoadingState() {
    // Hide loading for cart upsell card
    const upsellCards = document.querySelectorAll('[data-cart-upsell]');
    upsellCards.forEach(card => {
      const loadingIndicator = card.querySelector('.cart-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.remove();
      }
    });

    // Hide loading for cart reviews
    const cartReviews = document.querySelectorAll('.CP_TE_Sitewide_07_V2');
    cartReviews.forEach(review => {
      const loadingIndicator = review.querySelector('.cart-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.remove();
      }
    });

    console.log('CartVisibilityManager: Hiding loading state for cart elements');
  }

  /**
   * Setup mutation observer to detect when cart content is re-rendered
   */
  setupMutationObserver() {
    // Prevent duplicate observers
    if (this.observers && this.observers.length > 0) {
      console.log('CartVisibilityManager: Mutation observers already set up, skipping');
      return;
    }

    // Look for cart content containers that might be re-rendered
    const cartContainers = document.querySelectorAll('[data-api-content], [data-cart-drawer], [data-cart-page]');
    
    if (cartContainers.length === 0) {
      console.log('CartVisibilityManager: No cart containers found for mutation observer');
      return;
    }

    console.log(`CartVisibilityManager: Setting up mutation observers for ${cartContainers.length} containers`);
    
    this.observers = [];
    
    cartContainers.forEach((container, index) => {
      const observer = new MutationObserver((mutations) => {
        let shouldCheckVisibility = false;
        let upsellCardAdded = false;
        let cartContentReplaced = false;
        
        mutations.forEach((mutation) => {
          // Check if cart upsell card was added or modified
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if the added node is a cart upsell card or contains one
                if (node.matches('[data-cart-upsell]') || node.querySelector('[data-cart-upsell]')) {
                  upsellCardAdded = true;
                  shouldCheckVisibility = true;
                }
                // Check if cart content was re-rendered
                if (node.matches('[data-api-content]') || node.querySelector('[data-api-content]')) {
                  cartContentReplaced = true;
                  shouldCheckVisibility = true;
                }
              }
            });
          }
        });
        
        if (shouldCheckVisibility) {
          console.log(`CartVisibilityManager: Cart content change detected - Upsell added: ${upsellCardAdded}, Content replaced: ${cartContentReplaced}`);
          // Small delay to ensure the new content is fully rendered
          setTimeout(() => {
            this.restoreVisibilityState();
            // Apply promotional item styles
            this.applyPromotionalItemStyles();
            // Only check visibility if cart is not updating
            if (!this.isCartUpdating) {
              console.log('CartVisibilityManager: Cart stable, checking visibility');
              this.checkVisibility();
            } else {
              console.log('CartVisibilityManager: Cart still updating, deferring visibility check');
            }
          }, 100);
        }
      });
      
      // Start observing
      observer.observe(container, {
        childList: true,
        subtree: true
      });
      
      // Store observer reference for cleanup
      this.observers.push(observer);
      console.log(`CartVisibilityManager: Observer ${index + 1} set up for container:`, container);
    });
  }

  /**
   * Restore visibility state after cart content re-rendering
   */
  restoreVisibilityState() {
    const upsellCards = document.querySelectorAll('[data-cart-upsell]');
    
    upsellCards.forEach(card => {
      const shouldBeVisible = card.dataset.shouldBeVisible === 'true';
      if (shouldBeVisible && card.classList.contains('hidden')) {
        console.log('CartVisibilityManager: Restoring upsell card visibility after re-render');
        card.classList.remove('hidden');
      } else if (!shouldBeVisible && !card.classList.contains('hidden')) {
        console.log('CartVisibilityManager: Restoring upsell card hidden state after re-render');
        card.classList.add('hidden');
      }
    });

    // Hide quantity selectors and show remove buttons for promotional items
    this.hidePromotionalItemQuantitySelectors();

    // After restoring state, schedule a final visibility check to ensure consistency
    setTimeout(() => {
      // Only check visibility if cart is not updating
      if (!this.isCartUpdating) {
        this.checkVisibility();
      }
    }, 500);
  }

  /**
   * Apply promotional item styles to all cart items
   */
  applyPromotionalItemStyles() {
    // Apply styles to existing items
    this.hidePromotionalItemQuantitySelectors();
    
    // Also check for any new items that might have been added
    setTimeout(() => {
      this.hidePromotionalItemQuantitySelectors();
    }, 100);
  }

  setupUpsellProductId() {
    // Get the upsell product ID from the cart upsell card
    const upsellCard = document.querySelector('[data-cart-upsell]');
    if (upsellCard) {
      this.upsellProductId = upsellCard.dataset.productId;
      console.log(`CartVisibilityManager: Upsell product ID set to: ${this.upsellProductId}`);
    } else {
      console.log('CartVisibilityManager: No upsell card found during setup');
    }
    
    // Setup variant selector functionality
    this.setupVariantSelector();
  }

  setupVariantSelector() {
    const variantSelects = document.querySelectorAll('[data-upsell-variant-select]');
    
    variantSelects.forEach(select => {
      // Set initial variant image
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption && selectedOption.dataset.variantImage) {
        this.updateProductImage(select.closest('[data-cart-upsell]'), selectedOption.dataset.variantImage);
      }
    });
  }

  handleVariantChange(select) {
    const selectedOption = select.options[select.selectedIndex];
    const upsellCard = select.closest('[data-cart-upsell]');
    const variantId = selectedOption.value;
    
    if (selectedOption && selectedOption.dataset.variantImage) {
      this.updateProductImage(upsellCard, selectedOption.dataset.variantImage);
    }

    // Update the hidden input fields in all forms within this upsell card
    this.updateFormVariant(upsellCard, variantId);
  }

  updateFormVariant(upsellCard, variantId) {
    // Update all hidden id inputs in forms within this upsell card
    const idInputs = upsellCard.querySelectorAll('input[name="id"]');
    idInputs.forEach(input => {
      input.value = variantId;
    });

    // Also update any form attributes that reference the variant
    const forms = upsellCard.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(form => {
      const formIdInputs = form.querySelectorAll('input[name="id"]');
      formIdInputs.forEach(input => {
        input.value = variantId;
      });
    });
  }

  updateProductImage(upsellCard, imageUrl) {
    const imageElement = upsellCard.querySelector('[data-upsell-image]');
    if (imageElement) {
      // Add fade out effect
      imageElement.style.opacity = '0';
      
      setTimeout(() => {
        imageElement.src = imageUrl;
        imageElement.style.opacity = '1';
      }, 150);
    }
  }

  setFormLoadingState(form, isLoading) {
    const buttons = form.querySelectorAll('[data-add-to-cart]');
    
    buttons.forEach(button => {
      if (isLoading) {
        button.disabled = true;
        button.classList.add('loading');
        
        // Ensure the button has the proper structure for loading state
        if (!button.querySelector('.btn__loader')) {
          const loader = document.createElement('div');
          loader.className = 'btn__loader';
          loader.innerHTML = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/></svg>';
          button.appendChild(loader);
        }
        
        // Ensure the button has the proper structure for text
        if (!button.querySelector('.btn__text')) {
          const text = document.createElement('span');
          text.className = 'btn__text';
          text.textContent = button.textContent;
          button.innerHTML = '';
          button.appendChild(text);
          
          // Re-add the loader
          const loader = document.createElement('div');
          loader.className = 'btn__loader';
          loader.innerHTML = '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/></svg>';
          button.appendChild(loader);
        }
      } else {
        button.disabled = false;
        button.classList.remove('loading');
      }
    });
  }

  /**
   * Fetch cart data from Shopify's cart API
   */
  async fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      
      // Validate that we have the expected cart data structure
      if (!cartData || typeof cartData.total_price === 'undefined') {
        console.log('CartVisibilityManager: Cart data incomplete, retrying...');
        // Retry once after a short delay
        await new Promise(resolve => setTimeout(resolve, 200));
        const retryResponse = await fetch('/cart.js');
        if (retryResponse.ok) {
          const retryData = await retryResponse.json();
          if (retryData && typeof retryData.total_price !== 'undefined') {
            return retryData;
          }
        }
        return null;
      }
      
      return cartData;
    } catch (error) {
      console.error('Error fetching cart data:', error);
      return null;
    }
  }

  /**
   * Fetch cart data with cache busting to ensure fresh data
   */
  async fetchCartDataFresh() {
    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const response = await fetch(`/cart.js?t=${timestamp}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      
      // Validate that we have the expected cart data structure
      if (!cartData || typeof cartData.total_price === 'undefined') {
        console.log('CartVisibilityManager: Fresh cart data incomplete, retrying...');
        // Retry once after a short delay
        await new Promise(resolve => setTimeout(resolve, 200));
        const retryResponse = await fetch(`/cart.js?t=${Date.now()}`);
        if (retryResponse.ok) {
          const retryData = await retryResponse.json();
          if (retryData && typeof retryData.total_price !== 'undefined') {
            return retryData;
          }
        }
        return null;
      }
      
      return cartData;
    } catch (error) {
      console.error('Error fetching fresh cart data:', error);
      return null;
    }
  }

  /**
   * Check if the upsell product is already in the cart
   */
  isUpsellProductInCart(cartData) {
    if (!this.upsellProductId || !cartData || !cartData.items) {
      console.log(`CartVisibilityManager: Cannot check upsell in cart - Product ID: ${this.upsellProductId}, Cart data: ${!!cartData}, Cart items: ${cartData?.items?.length || 0}`);
      return false;
    }

    // Check if any cart item matches the upsell product ID
    const isInCart = cartData.items.some(item => {
      // Check both variant ID and product ID
      return item.variant_id.toString() === this.upsellProductId.toString() ||
             item.product_id.toString() === this.upsellProductId.toString();
    });
    
    console.log(`CartVisibilityManager: Upsell product ${this.upsellProductId} in cart: ${isInCart}`);
    if (isInCart) {
      console.log('CartVisibilityManager: Cart items:', cartData.items.map(item => ({ id: item.id, variant_id: item.variant_id, product_id: item.product_id })));
    }
    
    return isInCart;
  }

  /**
   * Check if cart data is valid and ready
   */
  isCartDataValid(cartData) {
    return cartData && 
           typeof cartData.total_price !== 'undefined' && 
           Array.isArray(cartData.items) &&
           this.upsellProductId;
  }

  /**
   * Get cart value in cents
   */
  getCartValueInCents(cartData) {
    if (!cartData || !cartData.total_price) {
      return 0;
    }
    // Shopify returns total_price in cents
    return cartData.total_price;
  }

  /**
   * Check and update visibility for both cart upsell card and cart reviews
   */
  async checkVisibility() {
    // Prevent multiple simultaneous visibility checks
    if (this.isCheckingVisibility) {
      console.log('CartVisibilityManager: Visibility check already in progress, skipping...');
      return;
    }

    this.isCheckingVisibility = true;
    
    try {
      console.log('CartVisibilityManager: Starting visibility check...');
      
      const cartData = await this.fetchCartData();
      if (!this.isCartDataValid(cartData)) {
        console.log('CartVisibilityManager: Cart data not valid yet, will retry later');
        // If cart data isn't ready, schedule another check
        setTimeout(() => this.debouncedCheckVisibility(), 1000);
        return;
      }

      const cartValueInCents = this.getCartValueInCents(cartData);
      const isUpsellInCart = this.isUpsellProductInCart(cartData);
      
      console.log(`CartVisibilityManager: Cart value: ${cartValueInCents} cents, Threshold: ${this.cartValueThreshold}, Upsell in cart: ${isUpsellInCart}`);

      // Determine cart upsell card visibility
      const shouldShowUpsell = cartValueInCents >= this.cartValueThreshold && !isUpsellInCart;
      console.log(`CartVisibilityManager: Should show upsell: ${shouldShowUpsell} (Value >= Threshold: ${cartValueInCents >= this.cartValueThreshold}, Not in cart: ${!isUpsellInCart})`);
      
      // Update cart upsell card visibility
      this.updateUpsellCardVisibility(shouldShowUpsell);
      
      // Update cart reviews visibility (only show if upsell is not visible)
      this.updateCartReviewsVisibility(!shouldShowUpsell);
      
      console.log('CartVisibilityManager: Visibility check completed');
    } catch (error) {
      console.error('CartVisibilityManager: Error checking visibility:', error);
    } finally {
      this.isCheckingVisibility = false;
    }
  }

  /**
   * Check visibility with retry mechanism to ensure we get updated cart data
   */
  async checkVisibilityWithRetry(maxRetries = 3, initialDelay = 800) {
    let retryCount = 0;
    let delay = initialDelay;

    const attemptCheck = async () => {
      try {
        // Use fresh cart data to prevent caching issues
        const cartData = await this.fetchCartDataFresh();
        if (!this.isCartDataValid(cartData)) {
          throw new Error('Cart data not valid');
        }

        const cartValueInCents = this.getCartValueInCents(cartData);
        const isUpsellInCart = this.isUpsellProductInCart(cartData);
        
        console.log(`CartVisibilityManager: Cart value: ${cartValueInCents} cents, Upsell in cart: ${isUpsellInCart} (attempt ${retryCount + 1})`);

        // Determine cart upsell card visibility
        const shouldShowUpsell = cartValueInCents >= this.cartValueThreshold && !isUpsellInCart;
        
        // Update cart upsell card visibility
        this.updateUpsellCardVisibility(shouldShowUpsell);
        
        // Update cart reviews visibility (only show if upsell is not visible)
        this.updateCartReviewsVisibility(!shouldShowUpsell);

        return true; // Success
      } catch (error) {
        retryCount++;
        console.log(`CartVisibilityManager: Attempt ${retryCount} failed, ${error.message}`);
        
        if (retryCount < maxRetries) {
          // Exponential backoff
          delay = delay * 1.5;
          console.log(`CartVisibilityManager: Retrying in ${delay}ms...`);
          setTimeout(() => attemptCheck(), delay);
        } else {
          console.log('CartVisibilityManager: Max retries reached, using fallback check');
          this.checkVisibility(); // Fallback to regular check
        }
        return false;
      }
    };

    // Start the retry process
    setTimeout(() => attemptCheck(), initialDelay);
  }

  /**
   * Update cart upsell card visibility
   */
  updateUpsellCardVisibility(shouldShow) {
    // Only update visibility if cart is not currently updating
    if (this.isCartUpdating) {
      console.log(`CartVisibilityManager: Cart updating, deferring upsell visibility change to: ${shouldShow}`);
      return;
    }

    const upsellCards = document.querySelectorAll('[data-cart-upsell]');
    
    upsellCards.forEach(card => {
      if (shouldShow) {
        card.classList.remove('hidden');
        // Store the visibility state in a data attribute for persistence
        card.dataset.shouldBeVisible = 'true';
        console.log('CartVisibilityManager: Showing cart upsell card');
      } else {
        card.classList.add('hidden');
        // Store the visibility state in a data attribute for persistence
        card.dataset.shouldBeVisible = 'false';
        console.log('CartVisibilityManager: Hiding cart upsell card');
      }
    });
  }

  /**
   * Update cart reviews visibility
   */
  updateCartReviewsVisibility(shouldShow) {
    // Only update visibility if cart is not currently updating
    if (this.isCartUpdating) {
      console.log(`CartVisibilityManager: Cart updating, deferring cart reviews visibility change to: ${shouldShow}`);
      return;
    }

    const cartReviews = document.querySelectorAll('.CP_TE_Sitewide_07_V2');
    
    cartReviews.forEach(review => {
      if (shouldShow) {
        review.classList.remove('hidden');
        console.log('CartVisibilityManager: Showing cart reviews');
      } else {
        review.classList.add('hidden');
        console.log('CartVisibilityManager: Hiding cart reviews');
      }
    });
  }

  /**
   * Refresh cart data and update visibility
   */
  refreshCart() {
    // Dispatch custom event to refresh cart
    const refreshEvent = new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { source: 'visibility-manager' }
    });
    document.dispatchEvent(refreshEvent);
    
    // Also try to trigger cart drawer refresh if it exists
    if (window.theme && window.theme.cart) {
      try {
        window.theme.cart.getCart();
      } catch (e) {
        console.log('Cart refresh method not available');
      }
    }

    // Try to trigger cart refresh on the cart drawer if it exists
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    if (cartDrawer && cartDrawer.refreshCart) {
      try {
        cartDrawer.refreshCart();
      } catch (e) {
        console.log('Cart drawer refresh method not available');
      }
    }

    // Try to trigger cart refresh on the cart page if it exists
    const cartPage = document.querySelector('[data-cart-page]');
    if (cartPage && cartPage.refreshCart) {
      try {
        cartPage.refreshCart();
      } catch (e) {
        console.log('Cart page refresh method not available');
      }
    }

    // Schedule a visibility check after the refresh completes
    setTimeout(() => this.debouncedCheckVisibility(), 500);
  }

  /**
   * Manually check visibility (can be called from other scripts)
   */
  manualCheck() {
    this.checkVisibility();
  }

  /**
   * Cleanup observers and event listeners
   */
  cleanup() {
    if (this.observers) {
      this.observers.forEach(observer => observer.disconnect());
      this.observers = [];
    }
    if (this.visibilityCheckTimeout) {
      clearTimeout(this.visibilityCheckTimeout);
      this.visibilityCheckTimeout = null;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartVisibilityManager = new CartVisibilityManager();
});

// Also initialize for dynamically loaded content (like cart drawer)
document.addEventListener('cart:loaded', () => {
  window.cartVisibilityManager = new CartVisibilityManager();
});

// Export for potential use in other scripts
window.CartVisibilityManager = CartVisibilityManager;

// Also export a global function to manually check visibility
window.checkCartVisibility = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.manualCheck();
  }
};

// Export function to restore visibility state after re-rendering
window.restoreCartVisibilityState = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.restoreVisibilityState();
  }
};

// Export function to force visibility check with retry
window.checkCartVisibilityWithRetry = () => {
  if (window.cartVisibilityManager) {
    window.cartVisibilityManager.checkVisibilityWithRetry(3, 800);
  }
};

// Export function to manually check visibility for debugging
window.debugCartVisibility = () => {
  if (window.cartVisibilityManager) {
    console.log('CartVisibilityManager: Manual visibility check triggered');
    window.cartVisibilityManager.checkVisibility();
  }
};

// Export function to show current state
window.showCartVisibilityState = () => {
  if (window.cartVisibilityManager) {
    console.log('CartVisibilityManager: Current state:', {
      isCartUpdating: window.cartVisibilityManager.isCartUpdating,
      isCheckingVisibility: window.cartVisibilityManager.isCheckingVisibility,
      upsellProductId: window.cartVisibilityManager.upsellProductId,
      cartValueThreshold: window.cartVisibilityManager.cartValueThreshold
    });
  }
};

// Export function to manually reset cart updating state
window.resetCartUpdatingState = () => {
  if (window.cartVisibilityManager) {
    console.log('CartVisibilityManager: Manually resetting cart updating state');
    window.cartVisibilityManager.setCartUpdating(false);
    // Force a visibility check
    window.cartVisibilityManager.checkVisibility();
  }
};

// Export function to manually hide promotional item quantity selectors and show remove buttons
window.hidePromotionalQuantitySelectors = () => {
  if (window.cartVisibilityManager) {
    console.log('CartVisibilityManager: Manually hiding promotional item quantity selectors and showing remove buttons');
    window.cartVisibilityManager.hidePromotionalItemQuantitySelectors();
  }
};

// Export function to apply promotional item styles
window.applyPromotionalItemStyles = () => {
  if (window.cartVisibilityManager) {
    console.log('CartVisibilityManager: Manually applying promotional item styles');
    window.cartVisibilityManager.applyPromotionalItemStyles();
  }
};
